import { createModule } from "saga-slice";
import { call, put, select } from "redux-saga/effects";
import recouverementService from "../services/recouvrementService";

const initialState = {
  // ===== DATA =====
  summary: [],
  summaryAll: [],
  summaryPending: [],
  details: [],
  selectedDate: null,

  // ===== UI STATE =====
  loadingSummary: false,
  loadingSummaryAll: false,
  loadingSummaryPending: false,
  loadingDetails: false,
  exporting: false,
  error: null,

  // ===== FILTERS (source of truth for export & details fallback) =====
  dateDebut: null,
  dateFin: null,
  clientId: null,
  banque: null,
  query: null,
};

const recouvrementSlice = createModule({
  name: "recouvrement",
  initialState,

  reducers: {
    /* ===================== TRIGGERS ===================== */

    fetchSummary: (state, payload) => {
      state.loadingSummary = true;
      state.error = null;

      // persist filters
      if (payload?.dateDebut !== undefined) state.dateDebut = payload.dateDebut;
      if (payload?.dateFin !== undefined) state.dateFin = payload.dateFin;
      if (payload?.clientId !== undefined) state.clientId = payload.clientId;
      if (payload?.banque !== undefined) state.banque = payload.banque;
      if (payload?.query !== undefined) state.query = payload.query;
    },

    fetchSummaryAll: (state, payload) => {
      state.loadingSummaryAll = true;
      state.error = null;

      if (payload?.dateDebut !== undefined) state.dateDebut = payload.dateDebut;
      if (payload?.dateFin !== undefined) state.dateFin = payload.dateFin;
      if (payload?.clientId !== undefined) state.clientId = payload.clientId;
      if (payload?.banque !== undefined) state.banque = payload.banque;
      if (payload?.query !== undefined) state.query = payload.query;
    },

    fetchSummaryPending: (state, payload) => {
      state.loadingSummaryPending = true;
      state.error = null;

      if (payload?.dateDebut !== undefined) state.dateDebut = payload.dateDebut;
      if (payload?.dateFin !== undefined) state.dateFin = payload.dateFin;
      if (payload?.clientId !== undefined) state.clientId = payload.clientId;
      if (payload?.banque !== undefined) state.banque = payload.banque;
      if (payload?.query !== undefined) state.query = payload.query;
    },

    fetchDetails: (state, payload) => {
      state.loadingDetails = true;
      state.error = null;
      state.selectedDate = payload?.dateEcheance || null;
      state.details = [];
    },

    clearDetails: (state) => {
      state.details = [];
      state.selectedDate = null;
      state.loadingDetails = false;
    },

    exportExcel: (state) => {
      state.exporting = true;
      state.error = null;
    },

    /* ===================== RESULTS ===================== */

    summaryFetched: (state, payload) => {
      state.summary = payload || [];
      state.loadingSummary = false;
    },

    summaryAllFetched: (state, payload) => {
      state.summaryAll = payload || [];
      state.loadingSummaryAll = false;
    },

    summaryPendingFetched: (state, payload) => {
      state.summaryPending = payload || [];
      state.loadingSummaryPending = false;
    },

    detailsFetched: (state, payload) => {
      state.details = payload || [];
      state.loadingDetails = false;
    },

    exportFinished: (state) => {
      state.exporting = false;
    },

    error: (state, payload) => {
      // ✅ one error handler to stop all loaders
      state.loadingSummary = false;
      state.loadingSummaryAll = false;
      state.loadingSummaryPending = false;
      state.loadingDetails = false;
      state.exporting = false;
      state.error = payload || "An error occurred";
    },
  },

  sagas: (A) => ({
    /* ===== FETCH SUMMARY (generic) ===== */
    *[A.fetchSummary]({ payload }) {
      try {
        const state = yield select((s) => s.recouvrement);

        const params = {
          dateDebut: payload?.dateDebut ?? state.dateDebut,
          dateFin: payload?.dateFin ?? state.dateFin,
          clientId: payload?.clientId ?? state.clientId,
          banque: payload?.banque ?? state.banque,
          query: payload?.query ?? state.query,
          statut: payload?.statut ?? null,
        };

        const { data } = yield call(recouverementService.getChequesSummary, params);
        const result = data?.body ?? data ?? [];
        yield put(A.summaryFetched(result));
      } catch (e) {
        yield put(A.error(resolveError(e)));
      }
    },

    /* ===== FETCH SUMMARY ALL (EN_ATTENTE + ENCAISSE, exclude ANNULE in DB) ===== */
    *[A.fetchSummaryAll]({ payload }) {
      try {
        const state = yield select((s) => s.recouvrement);

        const params = {
          dateDebut: payload?.dateDebut ?? state.dateDebut,
          dateFin: payload?.dateFin ?? state.dateFin,
          clientId: payload?.clientId ?? state.clientId,
          banque: payload?.banque ?? state.banque,
          query: payload?.query ?? state.query,
          statut: null,
        };

        const { data } = yield call(recouverementService.getChequesSummary, params);
        const result = data?.body ?? data ?? [];
        yield put(A.summaryAllFetched(result));
      } catch (e) {
        yield put(A.error(resolveError(e)));
      }
    },

    /* ===== FETCH SUMMARY PENDING (à encaisser) ===== */
    *[A.fetchSummaryPending]({ payload }) {
      try {
        const state = yield select((s) => s.recouvrement);

        const params = {
          dateDebut: payload?.dateDebut ?? state.dateDebut,
          dateFin: payload?.dateFin ?? state.dateFin,
          clientId: payload?.clientId ?? state.clientId,
          banque: payload?.banque ?? state.banque,
          query: payload?.query ?? state.query,
          statut: "EN_ATTENTE",
        };

        const { data } = yield call(recouverementService.getChequesSummary, params);
        const result = data?.body ?? data ?? [];
        yield put(A.summaryPendingFetched(result));
      } catch (e) {
        yield put(A.error(resolveError(e)));
      }
    },

    /* ===== FETCH DETAILS (payload overrides state) ===== */
    *[A.fetchDetails]({ payload }) {
      try {
        const state = yield select((s) => s.recouvrement);

        const params = {
          dateEcheance: payload?.dateEcheance,
          clientId: payload?.clientId ?? state.clientId,
          banque: payload?.banque ?? state.banque,
          query: payload?.query ?? state.query,
          statut: payload?.statut ?? null, // null => ENCAISSE + EN_ATTENTE (selon ton repo/service)
        };

        const { data } = yield call(recouverementService.getChequesByDate, params);
        const result = data?.body ?? data ?? [];
        yield put(A.detailsFetched(result));
      } catch (e) {
        yield put(A.error(resolveError(e)));
      }
    },

    /* ===== EXPORT EXCEL (uses state filters, unless you pass payload in your service) ===== */
    *[A.exportExcel]({ payload }) {
      try {
        const state = yield select((s) => s.recouvrement);

        const params = {
          dateDebut: payload?.dateDebut ?? state.dateDebut,
          dateFin: payload?.dateFin ?? state.dateFin,
          clientId: payload?.clientId ?? state.clientId,
          banque: payload?.banque ?? state.banque,
          query: payload?.query ?? state.query,
          statut: payload?.statut ?? null,
        };

        const response = yield call(recouverementService.exportChequesExcel, params);

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "recouvrement-cheques.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();

        yield put(A.exportFinished());
      } catch (e) {
        yield put(A.error(resolveError(e)));
      }
    },
  }),
});

/* ===== ERROR HELPER ===== */
function resolveError(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "An error occurred"
  );
}

export default recouvrementSlice;
export const recouvrementActions = recouvrementSlice.actions;